import { ApiClient } from './utils/api';
import { AuthService } from './services/AuthService';
import { PurchaseService } from './services/PurchaseService';
import { TelemetryService } from './services/TelemetryService';
import { RemoteConfigService } from './services/RemoteConfigService';
import { SDKConfig, PurchaseItem, PurchaseURLOptions, LoginURLOptions, Transaction, TipURLOptions, StorageAPI, StorageOptions, AffiliateLinkOptions, CreateAffiliateLink } from './types';
import { handleStoreLink, parseDeepLinkParams, isValidURL } from './utils/url';
import { StorageService } from './services/StorageService';
import { AffiliateLinkService } from './services/AffiliateLinkService';

// Export types
export * from './types';

// API URLs for different environments
const API_URLS: Record<'development' | 'staging' | 'production', string> = {
  development: 'http://localhost:3000',
  staging: 'https://dev-dev.oncade.xyz',
  production: 'https://dev.oncade.xyz',
};

const STORE_URLS: Record<'development' | 'staging' | 'production', string> = {
  development: 'https://dev.oncade.xyz',
  staging: 'https://dev-store.oncade.xyz',
  production: 'https://oncade.gg',
};

/**
 * Oncade SDK for integrating game purchases and wagering
 */
export class OncadeSDK {
  private apiClient: ApiClient;
  private authService: AuthService;
  private purchaseService: PurchaseService;
  private telemetryService?: TelemetryService;
  private remoteConfigService?: RemoteConfigService;
  private config: SDKConfig;
  private apiUrl: string;
  private storageServiceFactory!: (opts: StorageOptions) => StorageService;
  private affiliateLinkService!: AffiliateLinkService;

  /**
   * Create a new instance of the Oncade SDK
   * @param config SDK configuration
   */
  constructor(config: SDKConfig) {
    this.config = config;
    const env = config.environment ?? 'production';
    this.apiUrl = API_URLS[env];

    /* core plumbing */
    this.apiClient = new ApiClient(this.apiUrl, config.apiKey, config.gameId, env);
    this.authService = new AuthService(this.apiClient);
    const storeBaseUrl = `${STORE_URLS[env]}`;
    this.purchaseService = new PurchaseService(this.apiClient, config.gameId, storeBaseUrl);

    if (config.telemetry?.enabled !== false) {
      this.telemetryService = new TelemetryService({
        apiClient: this.apiClient,
        authService: this.authService,
        flushIntervalMs: config.telemetry?.flushIntervalMs,
        maxBatchSize: config.telemetry?.maxBatchSize,
      });
    }

    if (config.remoteConfig?.enabled !== false) {
      this.remoteConfigService = new RemoteConfigService({
        apiClient: this.apiClient,
        pollIntervalMs: config.remoteConfig?.pollIntervalMs,
      });
    }

    // Initialize new services after all dependencies are set up
    this.storageServiceFactory = (opts: StorageOptions) => new StorageService(this.apiClient, opts);
    this.affiliateLinkService = new AffiliateLinkService(this.apiClient, this.config.gameId);
  }

  /**
   * Initialize the game server by verifying the game ID and API key
   * @returns Promise resolving to a boolean indicating success
   */
  async initialize(): Promise<boolean> {
    // Initialize the game server
    const ok = await this.authService.initializeGameServer(
      this.config.gameId,
      this.config.apiKey
    );

    if (ok) {
      await Promise.all([
        this.telemetryService?.start(),       // pulls build/platform context & primes queue
        this.remoteConfigService?.bootstrap() // fetches dev→test→prod staged config
      ]);
    }

    return ok;
  }

  /* ───────────────────────────────────
   *  PUBLIC TELEMETRY HELPERS
   * ─────────────────────────────────── */
  track<EventPayload extends Record<string, unknown>>(
    eventName: string,
    payload: EventPayload,
    options?: { flushImmediately?: boolean }
  ): void {
    this.telemetryService?.track(eventName, payload, options);
  }

  flushTelemetry(): Promise<void> {
    return this.telemetryService?.flush() ?? Promise.resolve();
  }

  /* ───────────────────────────────────
   *  PUBLIC REMOTE CONFIG HELPERS
   * ─────────────────────────────────── */
  /** Typed getter with optional fallback */
  async getConfig<T>(key: string, defaultValue?: T): Promise<T | undefined> {
    return this.remoteConfigService?.get<T>(key, defaultValue);
  }

  /** Get entire config store */
  async getAllConfig(): Promise<Record<string, unknown>> {
    return this.remoteConfigService?.getConfig() ?? {};
  }

  /** Subscribe to live changes for a single key */
  onConfigChange<T>(key: string, cb: (val: T) => void): () => void {
    return this.remoteConfigService?.subscribe<T>(key, cb) ?? (() => {});
  }

  /**
   * Get session information including user association status
   * @returns Promise resolving to session information including user ID if associated
   */
  async getSessionInfo(): Promise<{
    isValid: boolean;
    hasUserId: boolean;
    sessionToken: string | null;
  }> {
    const sessionInfo = await this.authService.getSessionInfo();
    return {
      ...sessionInfo,
      sessionToken: this.authService.getSessionToken()
    };
  }

  /**
   * Clear the current session data
   */
  clearSession(): void {
    this.authService.clearSession();
  }

  /**
   * Get the store catalog for the game
   * @returns Promise resolving to an array of purchasable items
   */
  async getStoreCatalog(): Promise<PurchaseItem[]> {
    return this.purchaseService.getCatalog();
  }

  /**
   * Get a specific item from the store
   * @param itemId Item ID to retrieve
   * @returns Promise resolving to the item or null if not found
   */
  async getStoreItem(itemId: string): Promise<PurchaseItem | null> {
    return this.purchaseService.getItem(itemId);
  }

  /**
   * Get a purchase URL for an item
   * @param options Purchase URL options
   * @returns The generated purchase URL or null if no valid session token exists
   */
  getPurchaseURL(options: Omit<PurchaseURLOptions, 'sessionToken'> & { sessionToken?: string }): string | null {
    // Get the current session token if not provided in options
    const sessionToken = options.sessionToken || this.authService.getSessionToken();

    // If no session token exists, return null as we can't create a valid purchase URL
    if (!sessionToken) {
      console.error('Cannot generate purchase URL: No valid session token exists');
      return null;
    }

    if (!options.itemId || options.itemId === '' || options.itemId === 'undefined') {
      throw new Error('Item ID is required to generate a purchase URL');
    }

    // If affiliateCode is not provided and not suppressed, get from AffiliateLinkService
    let affiliateCode = options.affiliateCode;
    if (!affiliateCode && !options.suppressAffiliateCode) {
      affiliateCode = this.affiliateLinkService.getReferrerAffiliateCode() || undefined;
    }

    // Create a new options object with the session token and affiliate code
    const optionsWithToken: PurchaseURLOptions = {
      ...options,
      sessionToken,
      ...(affiliateCode ? { affiliateCode } : {})
    };

    return this.purchaseService.generatePurchaseURL(optionsWithToken);
  }

  /**
   * Get purchased items for the current user
   * @returns Promise resolving to an array of purchased items
   */
  async getPurchasedItems(): Promise<PurchaseItem[]> {
    return this.purchaseService.getPurchasedItems();
  }

  /**
   * Get transaction history for the current user
   * @returns Promise resolving to an array of transactions
   */
  async getTransactionHistory(): Promise<Transaction[]> {
    return this.purchaseService.getTransactionHistory();
  }

  /**
   * Extract parameters from a store link
   * @param url URL to extract parameters from
   * @returns Object containing affiliateCode if present
   */
  handleStoreLink(url: string): { affiliateCode?: string } {
    return handleStoreLink(url);
  }

  /**
   * Parse parameters from a deep link URL
   * @param url URL to parse
   * @returns Object containing the parsed parameters
   */
  parseDeepLinkParams(url: string): Record<string, string> {
    return parseDeepLinkParams(url);
  }

  /**
   * Override the API URL (for testing purposes only)
   * @param url The URL to use for API calls
   */
  setApiUrlForTesting(url: string, storeUrl: string): void {
    this.apiUrl = url;

    // Recreate API client with new URL
    this.apiClient = new ApiClient(
      this.apiUrl,
      this.config.apiKey,
      this.config.gameId
    );

    // Update services
    this.authService = new AuthService(this.apiClient);

    // Update store base URL
    const storeBaseUrl = `${storeUrl}`;

    this.purchaseService = new PurchaseService(
      this.apiClient,
      this.config.gameId,
      storeBaseUrl
    );
  }

  /**
   * Get a login URL for user authentication and session association
   * @param options Login URL options
   * @returns The generated login URL
   */
  getLoginURL(options: LoginURLOptions): string {
    return this.authService.generateLoginURL(options);
  }

  /**
   * Get a tip URL to allow users to give a tip to the developer
   * @param options Tip URL options
   * @returns The generated tip URL or null if no valid session token exists
   */
  getTipURL(options: Omit<TipURLOptions, 'sessionToken'> & { sessionToken?: string }): string | null {
    // Get the current session token if not provided in options
    const sessionToken = options.sessionToken || this.authService.getSessionToken();

    // If no session token exists, return null as we can't create a valid tip URL
    if (!sessionToken) {
      console.error('Cannot generate tip URL: No valid session token exists');
      return null;
    }

    if (options.amount && options.amount <= 0) {
      throw new Error('Tip amount must be greater than 0');
    }

    if (options.redirectUrl && !isValidURL(options.redirectUrl)) {
      throw new Error('Invalid redirect URL');
    }

    // Create a new options object with the session token and gameId
    const optionsWithToken: TipURLOptions = {
      ...options,
      sessionToken,
      gameId: this.config.gameId
    };

    return this.purchaseService.generateTipURL(optionsWithToken);
  }

  /**
   * Storage API
   */
  storage(opts: StorageOptions): StorageAPI {
    return this.storageServiceFactory(opts);
  }

  /**
   * Create an affiliate link (fetches userId from API if needed)
   * @param opts Affiliate link options (targetUrl, gameId, etc.)
   * @returns Promise<string> affiliate link URL
   */
  async createAffiliateLink(opts: Omit<AffiliateLinkOptions, 'userId'>): Promise<string> {
    const userId = await this.resolveUserId();
    return this.affiliateLinkService.createLink({ ...opts, userId });
  }

  /**
   * Get the affiliate code you arrived with (from query param)
   */
  getReferrerAffiliateCode(): string | null {
    return this.affiliateLinkService.getReferrerAffiliateCode();
  }

  /**
   * Resolve the current user ID from the session token (internal helper)
   */
  private async resolveUserId(): Promise<string> {
    const sessionInfo = await this.getSessionInfo();
    if (!sessionInfo.isValid || !sessionInfo.sessionToken || !sessionInfo.hasUserId) {
      throw new Error('No valid session available. Please initialize the SDK first.');
    }
    
    const userId = await this.authService.getUserId();
    if (!userId) {
      throw new Error('User ID is missing from API response');
    }
    return userId;
  }

  /**
   * Get the affiliate code for the current user (async)
   */
  async getMyAffiliateCode(): Promise<string> {
    const userId = await this.resolveUserId();
    return this.affiliateLinkService.getMyAffiliateCode(userId);
  }

  /**
   * Wrap a URL with the current user's affiliate code (async)
   */
  public async wrapUrl(url: string): Promise<string> {
    const userId = await this.resolveUserId();
    return this.affiliateLinkService.wrapUrl(url, userId);
  }
}

// Export class directly for better ESM compatibility
export default OncadeSDK;